import { Component, computed, effect, forwardRef, inject, input } from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { Store } from '@ngrx/store';
import { FloatLabelModule } from '@openng/optimus-ui/floatlabel';
import { InputTextModule } from '@openng/optimus-ui/inputtext';
import { TextareaModule } from '@openng/optimus-ui/textarea';
import { loadCities, selectCities } from '@zayan-erp/stores';
import { DropdownData, GenericDropdownComponent } from '@zayan-erp/shared';
import { ADDRESS_TYPE_OPTIONS } from '../../enums/address-types.enum';
import { AddressFieldConfig } from '../../models/office.models';

type AddressField = 'addressLine1' | 'addressLine2' | 'suite' | 'postalCode' | 'cityId' | 'addressType';

const ADDRESS_FIELDS: AddressField[] = ['addressLine1', 'addressLine2', 'suite', 'postalCode', 'cityId', 'addressType'];

interface AddressFieldState {
  visible: boolean;
  required: boolean;
}

export interface AddressPrimengValue {
  addressLine1: string | null;
  addressLine2: string | null;
  suite: string | null;
  postalCode: string | null;
  cityId: string | null;
  addressType: string | null;
}

@Component({
  selector: 'app-addresses-primeng',
  imports: [ReactiveFormsModule, FloatLabelModule, InputTextModule, GenericDropdownComponent, TextareaModule],
  templateUrl: './addresses-primeng.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AddressesPrimengComponent),
      multi: true
    }
  ]
})
export class AddressesPrimengComponent implements ControlValueAccessor {
  readonly #store = inject(Store);

  readonly submitted = input<boolean>(false);
  readonly fieldConfig = input<AddressFieldConfig[]>([]);

  readonly addressForm = new FormGroup({
    addressLine1: new FormControl<string | null>(''),
    addressLine2: new FormControl<string | null>(''),
    suite: new FormControl<string | null>(''),
    postalCode: new FormControl<string | null>(''),
    cityId: new FormControl<string | null>(null),
    addressType: new FormControl<string | null>(null)
  });

  readonly fields = computed(() => {
    const configs = this.fieldConfig();

    return Object.fromEntries(
      ADDRESS_FIELDS.map(field => {
        const config = configs.find(item => item.field === field);

        return [field, { visible: !!config?.visible, required: !!config?.required }];
      })
    ) as Record<AddressField, AddressFieldState>;
  });

  readonly cities = this.#store.selectSignal(selectCities);
  readonly addressTypes: DropdownData[] = ADDRESS_TYPE_OPTIONS.map(option => ({
    id: option.value,
    firstLabel: option.label,
    secondLabel: null
  }));

  #onChange: (value: AddressPrimengValue) => void = () => undefined;
  #onTouched: () => void = () => undefined;

  constructor() {
    if (!this.cities().length) this.#store.dispatch(loadCities());

    this.addressForm.valueChanges.subscribe(() => this.#onChange(this.addressForm.getRawValue()));

    effect(() => this.#updateValidators(this.fields()));
  }

  writeValue(value: Partial<AddressPrimengValue> | null): void {
    if (value) {
      this.addressForm.patchValue(value, { emitEvent: false });
    } else {
      this.addressForm.reset({}, { emitEvent: false });
    }
  }

  registerOnChange(fn: (value: AddressPrimengValue) => void): void {
    this.#onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.#onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.addressForm.disable({ emitEvent: false });
    } else {
      this.addressForm.enable({ emitEvent: false });
    }
  }

  onFieldTouched(): void {
    this.#onTouched();
  }

  getFormValue(): AddressPrimengValue {
    return this.addressForm.getRawValue();
  }

  isValid(): boolean {
    return this.addressForm.valid;
  }

  #updateValidators(fields: Record<AddressField, AddressFieldState>): void {
    ADDRESS_FIELDS.forEach(field => {
      const control = this.addressForm.controls[field];
      const { visible, required } = fields[field];

      control.setValidators(visible && required ? [Validators.required] : []);
      control.updateValueAndValidity({ emitEvent: false });
    });
  }
}
